/**
 * View Actions
 *
 * Business logic for view/camera operations.
 */

import type { CanvasEditor } from "@rool/editor";
import { fitToNodes as computeFitToNodes } from "@rool/renderer/geometry";

/**
 * Zoom in by a fixed factor (25%).
 */
export function zoomIn(editor: CanvasEditor): void {
	const canvas = editor.getCanvas();
	if (!canvas) return;
	const viewport = canvas.getViewport();
	canvas.zoom(1.25, viewport.width / 2, viewport.height / 2);
}

/**
 * Zoom out by a fixed factor (20%).
 */
export function zoomOut(editor: CanvasEditor): void {
	const canvas = editor.getCanvas();
	if (!canvas) return;
	const viewport = canvas.getViewport();
	canvas.zoom(0.8, viewport.width / 2, viewport.height / 2);
}

/**
 * Reset zoom to 100%.
 */
export function resetZoom(editor: CanvasEditor): void {
	const canvas = editor.getCanvas();
	if (!canvas) return;
	const cam = canvas.getCamera();
	canvas.setCamera({ ...cam, zoom: 1 });
}

/**
 * Fit view to all nodes (or selected nodes if any), with animation.
 * Caps zoom at 1 so the view never zooms in beyond 100%.
 */
export function fitToContent(editor: CanvasEditor): void {
	const canvas = editor.getCanvas();
	if (!canvas) return;

	const selection = editor.getSelection();
	const allNodes = editor.getNodes();
	const nodeIds = selection.length > 0 ? selection : allNodes.map((n) => n.id);
	if (nodeIds.length === 0) return;

	const targetNodes = allNodes.filter((n) => nodeIds.includes(n.id));
	if (targetNodes.length === 0) return;

	const viewport = canvas.getViewport();
	const target = computeFitToNodes(
		targetNodes.map((n) => ({ position: n.position, size: n.size })),
		viewport,
		80,
		0.1,
		1,
	);

	editor.animateCamera(target);
}

/**
 * Reset view to origin at 100% zoom.
 */
export function resetView(editor: CanvasEditor): void {
	editor.resetView();
}
